/**
 * Turns an axios/API error into a localized, human message.
 *
 * - Known backend `detail` strings (English or snake_case codes) are translated FR/EN.
 * - 502/503/504 or no response at all => "service temporarily unavailable" (deploy/rollout, network).
 * - Axios timeouts => explicit timeout message.
 * - FastAPI 422 validation payloads (array of {msg}) are flattened instead of showing "[object Object]".
 *
 * Returns { kind, message, code } where kind is one of:
 * 'unavailable' | 'timeout' | 'network' | 'conflict' | 'auth' | 'validation' | 'forbidden' | 'not_found' | 'unknown'
 */

const DETAIL_MESSAGES = {
  'Email already registered': {
    kind: 'conflict',
    fr: 'Un compte existe d\u00e9j\u00e0 avec cet email. Connectez-vous ou utilisez une autre adresse.',
    en: 'An account already exists with this email. Sign in or use another address.',
  },
  'Invalid credentials': {
    kind: 'auth',
    fr: 'Email ou mot de passe incorrect.',
    en: 'Incorrect email or password.',
  },
  'User not found': {
    kind: 'auth',
    fr: 'Compte introuvable. Reconnectez-vous.',
    en: 'Account not found. Please sign in again.',
  },
  'Token expired': {
    kind: 'auth',
    fr: 'Votre session a expir\u00e9. Reconnectez-vous.',
    en: 'Your session has expired. Please sign in again.',
  },
  'Invalid token': {
    kind: 'auth',
    fr: 'Session invalide. Reconnectez-vous.',
    en: 'Invalid session. Please sign in again.',
  },
  'Not authenticated': {
    kind: 'auth',
    fr: 'Vous devez \u00eatre connect\u00e9 pour continuer.',
    en: 'You need to be signed in to continue.',
  },
  'Admin access required': {
    kind: 'forbidden',
    fr: 'Acc\u00e8s r\u00e9serv\u00e9 \u00e0 l\u2019administrateur.',
    en: 'Administrator access required.',
  },
  'Profile required for analysis': {
    kind: 'validation',
    fr: 'Compl\u00e9tez votre profil avant de lancer une analyse.',
    en: 'Complete your profile before running an analysis.',
  },
  'Empty photo': {
    kind: 'validation',
    fr: 'La photo est vide ou illisible.',
    en: 'The photo is empty or unreadable.',
  },
  'Photo too large (max 10MB)': {
    kind: 'validation',
    fr: 'Photo trop lourde (10 Mo maximum).',
    en: 'Photo too large (10 MB maximum).',
  },
  'No routine found': {
    kind: 'not_found',
    fr: 'Aucune routine g\u00e9n\u00e9r\u00e9e pour le moment.',
    en: 'No routine generated yet.',
  },
  'Simulation not found': {
    kind: 'not_found',
    fr: 'Simulation introuvable.',
    en: 'Simulation not found.',
  },
  'Job not found': {
    kind: 'not_found',
    fr: 'T\u00e2che introuvable ou expir\u00e9e.',
    en: 'Task not found or expired.',
  },
  photo_consent_required: {
    kind: 'validation',
    fr: 'Acceptez le traitement de vos photos pour continuer.',
    en: 'Accept photo processing to continue.',
  },
  too_many_messages: {
    kind: 'validation',
    fr: 'Trop de messages envoy\u00e9s. R\u00e9essayez un peu plus tard.',
    en: 'Too many messages sent. Please try again a little later.',
  },
  invalid_contact_email: {
    kind: 'validation',
    fr: 'Adresse email de contact invalide.',
    en: 'Invalid contact email address.',
  },
  guest_no_email: {
    kind: 'validation',
    fr: 'Les comptes invit\u00e9s n\u2019ont pas d\u2019adresse email : cr\u00e9ez un compte pour recevoir des emails.',
    en: 'Guest accounts have no email address: create an account to receive emails.',
  },
  no_recipient_email: {
    kind: 'validation',
    fr: 'Aucune adresse email de destination.',
    en: 'No recipient email address.',
  },
};

const GENERIC = {
  unavailable: {
    fr: 'Le serveur est momentan\u00e9ment indisponible (mise \u00e0 jour en cours). R\u00e9essayez dans quelques secondes.',
    en: 'The server is temporarily unavailable (update in progress). Please try again in a few seconds.',
  },
  timeout: {
    fr: 'Le serveur met trop de temps \u00e0 r\u00e9pondre. V\u00e9rifiez votre connexion et r\u00e9essayez.',
    en: 'The server is taking too long to respond. Check your connection and try again.',
  },
  network: {
    fr: 'Impossible de joindre le serveur. V\u00e9rifiez votre connexion internet.',
    en: 'Unable to reach the server. Check your internet connection.',
  },
  validation: {
    fr: 'Certaines informations sont invalides. V\u00e9rifiez le formulaire.',
    en: 'Some information is invalid. Please check the form.',
  },
  server: {
    fr: 'Erreur interne du serveur. R\u00e9essayez dans un instant.',
    en: 'Internal server error. Please try again shortly.',
  },
  unknown: {
    fr: 'Une erreur est survenue. R\u00e9essayez.',
    en: 'An error occurred. Please try again.',
  },
};

const pick = (entry, lang) => entry[lang === 'en' ? 'en' : 'fr'];

/** Flattens a FastAPI 422 `detail` array into a short readable sentence. */
const flattenValidation = (detail, lang) => {
  if (!Array.isArray(detail)) return null;
  const fields = detail
    .map((d) => (Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : null))
    .filter((f) => f && f !== 'body');
  const emailIssue = detail.some((d) => String(d?.msg || '').toLowerCase().includes('email'));
  if (emailIssue) return lang === 'en' ? 'Please enter a valid email address.' : 'Saisissez une adresse email valide.';
  if (fields.length) {
    const list = [...new Set(fields)].join(', ');
    return lang === 'en' ? `Invalid or missing field: ${list}.` : `Champ invalide ou manquant : ${list}.`;
  }
  return pick(GENERIC.validation, lang);
};

export const describeApiError = (err, lang = 'fr') => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = typeof detail === 'string' ? detail : null;

  // Known backend detail => translated message
  if (code && DETAIL_MESSAGES[code]) {
    return { kind: DETAIL_MESSAGES[code].kind, message: pick(DETAIL_MESSAGES[code], lang), code };
  }

  // Gateway / rollout / overloaded
  if (status === 502 || status === 503 || status === 504) {
    return { kind: 'unavailable', message: pick(GENERIC.unavailable, lang), code };
  }

  // No HTTP response at all
  if (!err?.response) {
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
      return { kind: 'timeout', message: pick(GENERIC.timeout, lang), code: null };
    }
    return { kind: 'network', message: pick(GENERIC.network, lang), code: null };
  }

  if (status === 422) {
    return { kind: 'validation', message: flattenValidation(detail, lang) || pick(GENERIC.validation, lang), code };
  }

  if (status >= 500) {
    return { kind: 'server', message: pick(GENERIC.server, lang), code };
  }

  // Unknown 4xx with a readable string detail: show it as-is (already user-facing)
  if (code) {
    const kind = status === 401 ? 'auth' : status === 403 ? 'forbidden' : status === 404 ? 'not_found' : status === 409 ? 'conflict' : 'unknown';
    return { kind, message: code, code };
  }

  return { kind: 'unknown', message: pick(GENERIC.unknown, lang), code: null };
};

/** Convenience: just the message string. */
export const apiErrorMessage = (err, lang = 'fr', fallback = null) => {
  const { kind, message } = describeApiError(err, lang);
  return kind === 'unknown' && fallback ? fallback : message;
};
